import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageSquare, ArrowLeft, Search, CheckCheck, UserPlus, Send, Trash2, RefreshCw, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

const STATUS_TABS = [
  { value: 'all', label: 'All', color: 'gray' },
  { value: 'unread', label: 'Unread', color: 'blue' },
  { value: 'read', label: 'Read', color: 'gray' },
  { value: 'replied', label: 'Replied', color: 'green' },
  { value: 'lead_created', label: 'Lead Created', color: 'purple' },
];

export default function WhatsAppInbox({ user }) {
  const navigate = useNavigate();
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [replyModal, setReplyModal] = React.useState(null);
  const [replyText, setReplyText] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [replyTemplates, setReplyTemplates] = React.useState([]);
  const [createLeadModal, setCreateLeadModal] = React.useState(null);
  const [leadData, setLeadData] = React.useState({});

  React.useEffect(() => { fetchMessages(); }, [page, statusFilter]); // eslint-disable-line
  React.useEffect(() => { fetchInboxConfig(); }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const resp = await axios.get(`${BACKEND_URL}/api/whatsapp/inbox?${params}`, getAuth());
      setMessages(resp.data.messages || []);
      setTotal(resp.data.total || 0);
      setUnreadCount(resp.data.unread_count || 0);
      setTotalPages(resp.data.total_pages || 1);
      setPage(resp.data.page || 1);
    } catch (e) {
      console.error('Failed to fetch inbox:', e);
    }
    setLoading(false);
  };

  const fetchInboxConfig = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/whatsapp/inbox-config`, getAuth());
      setReplyTemplates(resp.data.reply_templates || []);
    } catch { /* silent */ }
  };

  const handleMarkRead = async (msgId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/inbox/${msgId}/mark-read`, {}, getAuth());
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, status: 'read' } : m));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { toast.error('Failed to mark as read'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/inbox/mark-all-read`, {}, getAuth());
      toast.success('All messages marked as read');
      fetchMessages();
    } catch { toast.error('Failed'); }
  };

  const handleReply = async () => {
    if (!replyText.trim()) { toast.error('Enter a reply message'); return; }
    setSending(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/whatsapp/inbox/${replyModal.id}/reply`, { message: replyText }, getAuth());
      if (resp.data.status === 'sent') {
        toast.success('Reply sent!');
        setMessages(prev => prev.map(m => m.id === replyModal.id ? { ...m, status: 'replied', replied_with: replyText } : m));
        setReplyModal(null);
        setReplyText('');
      } else {
        toast.error(resp.data.error || 'Failed to send reply');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    }
    setSending(false);
  };

  const handleCreateLead = async () => {
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/whatsapp/inbox/${createLeadModal.id}/create-lead`, leadData, getAuth());
      if (resp.data.status === 'created') {
        toast.success(`Lead #${resp.data.lead_no} created!`);
        setMessages(prev => prev.map(m => m.id === createLeadModal.id ? { ...m, status: 'lead_created', lead_id: resp.data.lead_id } : m));
      } else if (resp.data.status === 'existing_lead') {
        toast.info(resp.data.message);
        setMessages(prev => prev.map(m => m.id === createLeadModal.id ? { ...m, status: 'lead_created', lead_id: resp.data.lead_id } : m));
      }
      setCreateLeadModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/whatsapp/inbox/${msgId}`, getAuth());
      setMessages(prev => prev.filter(m => m.id !== msgId));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  const openCreateLeadModal = (msg) => {
    const nameParts = (msg.contact_name || '').split(' ', 2);
    setLeadData({
      first_name: nameParts[0] || '',
      last_name: nameParts[1] || '',
      lead_source: 'WhatsApp',
      sub_source: 'WhatsApp Inbox',
      stage: 'new',
      priority: 'warm',
      assigned_to: '',
      interested_brand: '',
      interested_model: '',
    });
    setCreateLeadModal(msg);
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusBadge = (s) => {
    const map = {
      unread: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Unread' },
      read: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Read' },
      replied: { bg: 'bg-green-100', text: 'text-green-700', label: 'Replied' },
      lead_created: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Lead Created' },
    };
    const c = map[s] || map.read;
    return <span className={`${c.bg} ${c.text} text-[10px] font-semibold px-2 py-0.5 rounded-full`}>{c.label}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/modules')} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="inbox-back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <h1 className="text-lg font-bold text-gray-800">WhatsApp Inbox</h1>
              {unreadCount > 0 && <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="px-3 py-1.5 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium flex items-center gap-1" data-testid="inbox-mark-all-read">
                <CheckCheck className="w-3.5 h-3.5" /> Mark All Read
              </button>
            )}
            <button onClick={fetchMessages} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="inbox-refresh">
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
            <button onClick={() => navigate('/settings/whatsapp')} className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-medium" data-testid="inbox-settings-btn">
              Settings
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-4">
        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 mb-4 space-y-3" data-testid="inbox-filters">
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_TABS.map(t => (
              <button key={t.value} onClick={() => { setStatusFilter(t.value); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${statusFilter === t.value ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`inbox-tab-${t.value}`}>
                {t.label} {t.value === 'unread' && unreadCount > 0 && `(${unreadCount})`}
              </button>
            ))}
            <div className="flex-1" />
            <span className="text-xs text-gray-400">Total: {total}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchMessages()} placeholder="Search phone, name, message..." className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="inbox-search" />
            </div>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="inbox-date-from" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="inbox-date-to" />
            <button onClick={() => { setPage(1); fetchMessages(); }} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1" data-testid="inbox-apply-filter">
              <Filter className="w-3.5 h-3.5" /> Apply
            </button>
          </div>
        </div>

        {/* Message List */}
        <div className="space-y-2">
          {loading ? (
            <div className="bg-white rounded-xl border p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" />
            </div>
          ) : messages.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No messages found</p>
              <p className="text-gray-300 text-xs mt-1">Configure webhook in WhatsApp Settings to start receiving messages</p>
            </div>
          ) : messages.map(msg => (
            <div key={msg.id} className={`bg-white rounded-xl border p-4 transition-all ${msg.status === 'unread' ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''}`} data-testid={`inbox-msg-${msg.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm flex-shrink-0">
                    {(msg.contact_name || msg.phone)?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-800">{msg.contact_name || 'Unknown'}</span>
                      <span className="text-xs text-gray-400">{msg.phone}</span>
                      {statusBadge(msg.status)}
                    </div>
                    <p className="text-sm text-gray-700 mt-1 leading-relaxed">{msg.message}</p>
                    {msg.replied_with && (
                      <div className="mt-2 pl-3 border-l-2 border-green-300">
                        <p className="text-xs text-green-600 font-medium">Replied{msg.replied_by ? ` by ${msg.replied_by}` : ''}:</p>
                        <p className="text-xs text-green-700">{msg.replied_with}</p>
                      </div>
                    )}
                    {msg.lead_id && (
                      <div className="mt-1">
                        <button onClick={() => navigate(`/leads/edit/${msg.lead_id}`)} className="text-xs text-purple-600 hover:underline font-medium">
                          View Lead →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className="text-[10px] text-gray-400">{timeAgo(msg.received_at)}</span>
                  <div className="flex items-center gap-1">
                    {msg.status === 'unread' && (
                      <button onClick={() => handleMarkRead(msg.id)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Mark Read" data-testid={`msg-mark-read-${msg.id}`}>
                        <CheckCheck className="w-4 h-4 text-blue-500" />
                      </button>
                    )}
                    {msg.status !== 'lead_created' && (
                      <button onClick={() => { setReplyText(''); setReplyModal(msg); }} className="p-1.5 hover:bg-green-50 rounded-lg" title="Reply" data-testid={`msg-reply-${msg.id}`}>
                        <Send className="w-4 h-4 text-green-600" />
                      </button>
                    )}
                    {!msg.lead_id && (
                      <button onClick={() => openCreateLeadModal(msg)} className="p-1.5 hover:bg-purple-50 rounded-lg" title="Create Lead" data-testid={`msg-create-lead-${msg.id}`}>
                        <UserPlus className="w-4 h-4 text-purple-600" />
                      </button>
                    )}
                    <button onClick={() => handleDelete(msg.id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Delete" data-testid={`msg-delete-${msg.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
          </div>
        )}
      </div>

      {/* Reply Modal */}
      {replyModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setReplyModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()} data-testid="reply-modal">
            <div className="px-5 py-3 border-b bg-green-700 text-white rounded-t-2xl font-semibold flex items-center justify-between">
              <span>Reply to {replyModal.contact_name || replyModal.phone}</span>
              <button onClick={() => setReplyModal(null)} className="text-white/70 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Original message:</p>
                <p className="text-sm text-gray-700 mt-1">{replyModal.message}</p>
              </div>
              {replyTemplates.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Quick replies:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {replyTemplates.map((tpl, i) => (
                      <button key={i} onClick={() => setReplyText(tpl)} className="text-xs px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 border border-green-200" data-testid={`reply-template-${i}`}>
                        {tpl.length > 50 ? tpl.substring(0, 50) + '...' : tpl}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={4} className="w-full px-3 py-2 border rounded-xl text-sm resize-none" placeholder="Type your reply..." data-testid="reply-text" />
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setReplyModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button onClick={handleReply} disabled={sending} className="px-5 py-2 bg-green-700 text-white text-sm rounded-xl hover:bg-green-600 disabled:opacity-50 flex items-center gap-1.5" data-testid="reply-send-btn">
                <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Lead Modal */}
      {createLeadModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCreateLeadModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()} data-testid="create-lead-modal">
            <div className="px-5 py-3 border-b bg-purple-700 text-white rounded-t-2xl font-semibold flex items-center justify-between">
              <span>Create Lead from WhatsApp</span>
              <button onClick={() => setCreateLeadModal(null)} className="text-white/70 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p className="text-gray-500">{createLeadModal.phone} — &ldquo;{createLeadModal.message}&rdquo;</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">First Name</label>
                  <input type="text" value={leadData.first_name} onChange={e => setLeadData(p => ({ ...p, first_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lead-first-name" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Last Name</label>
                  <input type="text" value={leadData.last_name} onChange={e => setLeadData(p => ({ ...p, last_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lead-last-name" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Lead Source</label>
                  <input type="text" value={leadData.lead_source} onChange={e => setLeadData(p => ({ ...p, lead_source: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Priority</label>
                  <select value={leadData.priority} onChange={e => setLeadData(p => ({ ...p, priority: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="lead-priority">
                    <option value="hot">Hot</option>
                    <option value="warm">Warm</option>
                    <option value="cold">Cold</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Brand</label>
                  <input type="text" value={leadData.interested_brand} onChange={e => setLeadData(p => ({ ...p, interested_brand: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Model</label>
                  <input type="text" value={leadData.interested_model} onChange={e => setLeadData(p => ({ ...p, interested_model: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setCreateLeadModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button onClick={handleCreateLead} className="px-5 py-2 bg-purple-700 text-white text-sm rounded-xl hover:bg-purple-600 flex items-center gap-1.5" data-testid="lead-create-btn">
                <UserPlus className="w-4 h-4" /> Create Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
